import {Integer, Show, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {Fragment, type ReactNode, useRef, useState} from 'react';

import {useDashboardAdaptiveDensityParticipant} from '../../hooks/useDashboardAdaptiveDensityParticipant';
import {DashboardAdaptiveDensityParticipant} from '../../types/DashboardAdaptiveDensityParticipant';
import {DashboardStretchRegion} from '../DashboardStretchRegion';

export interface DashboardTileRowRenderOptions {
  isProbe: boolean;
}

interface DashboardTileRowsProps<TRow> {
  id: string;
  rows: TRow[];
  getRowKey: (row: TRow) => string;
  getRowLayoutKey: (row: TRow) => string;
  renderRow: (row: TRow, options: DashboardTileRowRenderOptions) => ReactNode;
  naturalRows: number;
  spacing: Integer;
  columns: Integer;
}

interface StyledRowsProps {
  $spacing: Integer;
}

interface StyledVisualRowProps extends StyledRowsProps {
  $columns: Integer;
}

const StyledNaturalRows = styled.div<StyledRowsProps>`
  display: flex;
  flex-direction: column;
  gap: ${({$spacing, theme}) => theme.getSize($spacing)};
`;

const StyledAdaptiveRows = styled.div<StyledRowsProps>`
  display: flex;
  flex-direction: column;
  gap: ${({$spacing, theme}) => theme.getSize($spacing)};
  padding-top: ${({$spacing, theme}) => theme.getSize($spacing)};
`;

const StyledVisualRow = styled.div<StyledVisualRowProps>`
  display: grid;
  grid-template-columns: repeat(${({$columns}) => $columns}, minmax(0, 1fr));
  gap: ${({$spacing, theme}) => theme.getSize($spacing)};
  min-width: 0;
`;

const StyledProbeOuter = styled.div`
  position: absolute;
  inset: 0;
  height: 0;
  contain: size;
  visibility: hidden;
  pointer-events: none;
`;

const toVisualRows = <TRow,>(rows: TRow[], columns: number) => {
  const visualRows: TRow[][] = [];

  for (let index = 0; index < rows.length; index += columns) {
    visualRows.push(rows.slice(index, index + columns));
  }

  return visualRows;
};

const renderVisualRow = <TRow,>(
  row: TRow[],
  rowIndex: number,
  props: DashboardTileRowsProps<TRow>,
  options: DashboardTileRowRenderOptions
) => (
  <StyledVisualRow
    key={row.map(props.getRowKey).join(':')}
    $columns={props.columns}
    $spacing={props.spacing}
    data-dashboard-adaptive-row-index={options.isProbe ? undefined : rowIndex}
    data-dashboard-adaptive-probe-index={options.isProbe ? rowIndex : undefined}
  >
    {row.map((record) => (
      <Fragment key={props.getRowKey(record)}>{props.renderRow(record, options)}</Fragment>
    ))}
  </StyledVisualRow>
);

const renderProbeVisualRow = <TRow,>(
  row: TRow[] | undefined,
  rowIndex: number,
  props: DashboardTileRowsProps<TRow>
) => (row === undefined ? null : renderVisualRow(row, rowIndex, props, {isProbe: true}));

export function DashboardTileRows<TRow>(props: DashboardTileRowsProps<TRow>) {
  const visualRows = toVisualRows(props.rows, props.columns);
  const baselineRows = Math.min(props.naturalRows, visualRows.length);
  const [requestedVisibleRows, setRequestedVisibleRows] = useState(baselineRows);
  const [requestedProbeRow, setRequestedProbeRow] = useState(baselineRows);
  const visibleRows = Math.min(Math.max(requestedVisibleRows, baselineRows), visualRows.length);
  const probeRow = Math.min(Math.max(requestedProbeRow, baselineRows), visualRows.length);
  const naturalRowsRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const adaptiveRowsRef = useRef<HTMLDivElement>(null);

  const rowLayoutKeys = visualRows.map((row) => row.map(props.getRowLayoutKey).join(':'));
  const layoutSignature = JSON.stringify([
    props.columns,
    props.spacing,
    props.naturalRows,
    rowLayoutKeys,
  ]);
  const participant: DashboardAdaptiveDensityParticipant = {
    id: props.id,
    naturalRows: baselineRows,
    totalRows: visualRows.length,
    rowLayoutKeys,
    layoutSignature,
    getElements: () => ({
      naturalRows: naturalRowsRef.current,
      region: regionRef.current,
      adaptiveRows: adaptiveRowsRef.current,
    }),
    getVisibleRows: () => visibleRows,
    setVisibleRows: (nextVisibleRows) => {
      setRequestedVisibleRows(Math.min(Math.max(nextVisibleRows, baselineRows), visualRows.length));
    },
    getProbeRow: () => probeRow,
    setProbeRow: (nextProbeRow) => {
      setRequestedProbeRow(Math.min(Math.max(nextProbeRow, baselineRows), visualRows.length));
    },
  };
  const hasCoordinator = useDashboardAdaptiveDensityParticipant(participant);

  const naturalVisualRows = visualRows.slice(0, baselineRows);
  const adaptiveVisualRows = visualRows.slice(baselineRows, visibleRows);
  const probeVisualRow = hasCoordinator ? visualRows.at(probeRow) : undefined;

  return (
    <VStack height="100%" minHeight={0}>
      <StyledNaturalRows ref={naturalRowsRef} $spacing={props.spacing}>
        {naturalVisualRows.map((row, index) =>
          renderVisualRow(row, index, props, {isProbe: false})
        )}
      </StyledNaturalRows>
      <DashboardStretchRegion
        ref={regionRef}
        $spacing={props.spacing}
        data-dashboard-adaptive-region
      >
        <StyledAdaptiveRows ref={adaptiveRowsRef} $spacing={props.spacing}>
          {adaptiveVisualRows.map((row, index) =>
            renderVisualRow(row, baselineRows + index, props, {isProbe: false})
          )}
        </StyledAdaptiveRows>
        <Show when={probeVisualRow !== undefined}>
          <StyledProbeOuter inert aria-hidden>
            {renderProbeVisualRow(probeVisualRow, probeRow, props)}
          </StyledProbeOuter>
        </Show>
      </DashboardStretchRegion>
    </VStack>
  );
}
